import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { get } from '@/api/client';
import { NotFoundPage } from '@/pages/errors/NotFoundPage';
import { Avatar } from '@/components/atoms/Avatar';
import { LevelBadge } from '@/components/atoms/LevelBadge';
import { Button } from '@/components/atoms/Button';
import { Skeleton } from '@/components/atoms/Skeleton';
import { FollowToggle } from '@/components/molecules/FollowToggle';
import { ExpandableText } from '@/components/atoms/ExpandableText';
import { AdBanner } from '@/components/ads/AdBanner';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { MessageSquare } from 'lucide-react';

interface UserDetail {
  user_id: string;
  nickname: string;
  bio: string | null;
  profile_image_url: string | null;
  level: number;
  score: number;
  post_count: number;
  comment_count: number;
  follower_count: number;
  following_count: number;
  diary_count: number;
  interest_tags: string[] | null;
  invest_years_domestic: number | null;
  invest_years_usa: number | null;
  invest_years_coin: number | null;
  invest_years_etc: number | null;
}

const INTEREST_LABEL: Record<string, { label: string }> = {
  coin: { label: '코인' },
  stock: { label: '국내주식' },
  global: { label: '해외주식' },
  realestate: { label: '부동산' },
  macro: { label: '거시경제' },
  'ai-invest': { label: 'AI·퀀트' },
};

function formatInvestYears(years: number | null | undefined) {
  if (!years || years < 1) return null;
  if (years >= 10) return '10년 이상';
  return `${years}년차`;
}

const INVEST_CATEGORIES: Array<{
  key: 'invest_years_domestic' | 'invest_years_usa' | 'invest_years_coin' | 'invest_years_etc';
  label: string;
}> = [
  { key: 'invest_years_domestic', label: '국내주식' },
  { key: 'invest_years_usa', label: '미국주식' },
  { key: 'invest_years_coin', label: '코인' },
  { key: 'invest_years_etc', label: '기타' },
];

export function UserProfilePage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { me, isLoggedIn } = useAuthStore();
  const { setLoginModalOpen } = useUIStore();

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', userId],
    queryFn: () => get<UserDetail>(`/users/${userId}`),
    enabled: !!userId,
    retry: (count, err) => {
      const status = (err as any)?.response?.status;
      if (status === 404 || status === 400) return false;
      return count < 2;
    },
  });

  /* 존재하지 않는 유저 — 404 또는 잘못된 UUID(400) 시 NotFoundPage 렌더링 */
  const userNotFoundStatus = (error as any)?.response?.status;
  if (userNotFoundStatus === 404 || userNotFoundStatus === 400) {
    return <NotFoundPage />;
  }

  const isMe = !!me && !!data && me.userId === data.user_id;

  if (isLoading) return <Skeleton className="m-5 h-48" />;
  if (!data) return null;

  const handleSendMessage = () => {
    if (!isLoggedIn) return setLoginModalOpen(true);
    navigate(
      `/messages?to=${data.user_id}&nickname=${encodeURIComponent(data.nickname)}`,
    );
  };

  return (
    <div className="container-base py-10">
      <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
        {/* 아바타(좌) + 닉네임/레벨/bio(우) 한 행 구조 */}
        <div className="flex items-start gap-4">
          <Avatar
            src={data.profile_image_url ?? undefined}
            alt={data.nickname}
            size="lg"
            className="shrink-0"
          />
          <div className="min-w-0 flex-1 pt-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-xl font-bold">{data.nickname}</h1>
              <LevelBadge level={data.level} />
            </div>
            {data.bio && (
              <div className="mt-1">
                <ExpandableText
                  text={data.bio}
                  lines={2}
                  className="text-sm text-[var(--color-text-sub)]"
                />
              </div>
            )}
          </div>
        </div>

        {/* 관심분야 */}
        {!!data.interest_tags?.length && (
          <div className="mt-4">
            <div className="mb-1.5 text-xs text-[var(--color-text-sub)]">관심분야</div>
            <div className="flex flex-wrap items-center gap-1.5">
              {data.interest_tags.map((tag) => {
                const meta = INTEREST_LABEL[tag];
                if (!meta) return null;
                return (
                  <span
                    key={tag}
                    className="inline-flex items-center rounded-full bg-[var(--color-primary)]/10 px-2.5 py-1 text-xs font-medium text-[var(--color-primary)]"
                  >
                    {meta.label}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* 투자기간 (4개 분야별, 선택된 분야만 노출) */}
        {(() => {
          const investChips = INVEST_CATEGORIES.map((c) => ({
            label: c.label,
            years: formatInvestYears(data[c.key]),
          })).filter((c) => !!c.years);
          if (investChips.length === 0) return null;
          return (
            <div className="mt-3">
              <div className="mb-1.5 text-xs text-[var(--color-text-sub)]">투자기간</div>
              <div className="flex flex-wrap items-center gap-1.5">
                {investChips.map((c) => (
                  <span
                    key={c.label}
                    className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] bg-[var(--color-bg)] px-2.5 py-1 text-xs font-medium"
                  >
                    {c.label} {c.years}
                  </span>
                ))}
              </div>
            </div>
          );
        })()}

        {/* 액션 버튼 — 본인이 아닐 때 */}
        {!isMe && (
          <div className="mt-4 flex gap-2">
            <Button variant="secondary" fullWidth onClick={handleSendMessage}>
              <MessageSquare className="h-4 w-4" />
              쪽지 보내기
            </Button>
            <FollowToggle userId={data.user_id} variant="full" />
          </div>
        )}

        {/* 통계: 글 / 매매일지 / 팔로워 / 팔로잉 — 모바일 4등분 시 좁아 2등분 2줄로 전환 */}
        <div className="mt-6 grid grid-cols-2 gap-2 border-t border-[var(--color-border)] pt-4 text-center sm:grid-cols-4">
          <Link
            to={`/users/${data.user_id}/posts`}
            className="rounded-lg py-2 hover:bg-[var(--color-bg)]"
          >
            <div className="text-lg font-bold">{data.post_count}</div>
            <div className="text-xs text-[var(--color-text-sub)]">글</div>
          </Link>
          <Link
            to={`/users/${data.user_id}/diary`}
            className="rounded-lg py-2 hover:bg-[var(--color-bg)]"
          >
            <div className="text-lg font-bold">{data.diary_count ?? 0}</div>
            <div className="text-xs text-[var(--color-text-sub)]">매매일지</div>
          </Link>
          <Link
            to={`/users/${data.user_id}/followers`}
            className="rounded-lg py-2 hover:bg-[var(--color-bg)]"
          >
            <div className="text-lg font-bold">{data.follower_count}</div>
            <div className="text-xs text-[var(--color-text-sub)]">팔로워</div>
          </Link>
          <Link
            to={`/users/${data.user_id}/following`}
            className="rounded-lg py-2 hover:bg-[var(--color-bg)]"
          >
            <div className="text-lg font-bold">{data.following_count}</div>
            <div className="text-xs text-[var(--color-text-sub)]">팔로잉</div>
          </Link>
        </div>
      </section>

      {/* 페이지 하단 광고 — multiplex-h 는 프로필처럼 얇은 페이지에서 노필 잦음 → display-rect 로 교체 */}
      <AdBanner slot="display-rect" className="mt-4 sm:mt-8" />
    </div>
  );
}
